import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography
} from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import { Alert, AlertTitle } from "@material-ui/lab";
import Jimp from "jimp/es";
import moment from "moment";
import React, { useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import { GeneralContext } from "../../contexts/GeneralContext";
import { uploadRejection, uploadWarning } from "../../helpers/documentUpload";
import { reportErrorToNewRelic } from "../../helpers/newRelic";
import { getDocPreviewUrl } from "../../services/DocPreviewService";

const PhotoUpload = props => {
  const {
    LocalPaxData,
    setLocalPaxData,
    fileUpload,
    PaxsData,
    setPaxsData,
    fetchData,
    CIDData,
    LngLink
  } = useContext(GeneralContext);
  const [RedirectOn, setRedirectOn] = useState(false);
  const [Submitting, setSubmitting] = useState(false);
  const [Files, setFiles] = useState({ front: null, back: null });
  const [Previews, setPreviews] = useState({ front: null, back: null });
  const [ErrorValidation, setErrorValidation] = useState({
    front: null,
    back: null
  });
  // Motivo del rechazo que mandó api2, ya en el idioma del huésped.
  const [ErrorMessages, setErrorMessages] = useState({
    front: null,
    back: null
  });
  const [Loading, setLoading] = useState(false);
  // capture="environment" sólo abre la cámara en dispositivos táctiles; en
  // desktop se ignora y duplicaría el botón de subir.
  const HasCamera =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches;
  const fetchingPreview = useRef({});
  const frontInput = useRef();
  const backInput = useRef();
  const frontCameraInput = useRef();
  const backCameraInput = useRef();
  const [LoadingPreviews, setLoadingPreviews] = useState({
    front: false,
    back: false
  });
  useEffect(() => {
    if (!props.paxId) return;
    if (!PaxsData || !PaxsData.list) return;
    let currentLocalPax = LocalPaxData[props.paxId];
    if (!currentLocalPax) {
      let currentPax = PaxsData.list.find(pax => pax.paxid == props.paxId);
      if (!currentPax) {
        toast.error(<FormattedMessage id="paxForm.noData" />);
        return;
      }
      setLocalPaxData({ ...LocalPaxData, [props.paxId]: currentPax });
    }
    setLoading(false);
  }, [PaxsData]);

  useEffect(() => {
    if (!PaxsData) {
      setLoading(true);
      fetchData("PaxsData", CIDData.IDInbox)
        .then(res => {
          setPaxsData(res);
        })
        .catch(err => {
          toast.error(err);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Documento ya cargado: el listado de pax sólo indica si existe
  // (has_document_front / has_document_back); la imagen se pide aparte con una
  // URL de 60 s y se baja como blob para poder reenviarla.
  useEffect(() => {
    const pax = LocalPaxData[props.paxId];
    if (!pax) return;
    const exists = {
      front: Boolean(pax.has_document_front),
      back: Boolean(pax.has_document_back)
    };
    const apiSide = { front: "frente", back: "dorso" };
    ["front", "back"].forEach(side => {
      if (!exists[side] || Files[side] || Previews[side]) return;
      if (fetchingPreview.current[side]) return;
      fetchingPreview.current[side] = true;
      setLoadingPreviews(prev => ({ ...prev, [side]: true }));
      getDocPreviewUrl(props.paxId, apiSide[side])
        .then(url => (url ? fetch(url).then(res => res.blob()) : null))
        .then(blob => {
          if (!blob) return;
          setFiles(prev => (prev[side] ? prev : { ...prev, [side]: blob }));
          setPreviews(prev => ({ ...prev, [side]: URL.createObjectURL(blob) }));
        })
        .catch(err => {
          reportErrorToNewRelic("DOC_PREVIEW_FETCH", err);
        })
        .finally(() => {
          fetchingPreview.current[side] = false;
          setLoadingPreviews(prev => ({ ...prev, [side]: false }));
        });
    });
  }, [LocalPaxData]);

  const handleChange = (e, type) => {
    if (!e.target.files || !e.target.files[0]) return;
    setErrorMessages(prev => ({ ...prev, [type]: null }));
    let file_size = e.target.files[0].size;

    if (file_size > 8e6) {
      toast.error(<FormattedMessage id="photo.fileTooLarge" />);
      setErrorValidation({ ...ErrorValidation, [type]: "fileTooLarge" });
      return false;
    }

    const fileType = e.target.files[0].type;
    const supportedImageTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/bmp'
    ];

    if (!supportedImageTypes.includes(fileType)) {
      toast.error(<FormattedMessage id="photo.errors.unsupported_format" />);
      setErrorValidation({ ...ErrorValidation, [type]: "unsupported_format" });
      // Limpiar el input
      e.target.value = "";
      return false;
    }

    setLoadingPreviews({ ...LoadingPreviews, [type]: true });
    setErrorValidation({ ...ErrorValidation, [type]: null });

    let reader = new FileReader();
    let file = e.target.files[0];
    reader.readAsDataURL(file);
    reader.onload = () => {
      setLoadingPreviews({ front: false, back: false });
      setFiles({ ...Files, [type]: file });
      setPreviews({
        ...Previews,
        [type]: reader.result
      });
    };
  };

  const toTitleCase = str =>
    str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });

  async function processPhoto(side) {}

  async function submitDocs() {
    let processedFiles = { front: null, back: null };
    let reader = new FileReader();
    reader.readAsArrayBuffer(Files.front);
    reader.onloadend = function () {
      Jimp.read(reader.result)
        .then(image => {
          if (image.getWidth() > 1500) image.resize(1500, Jimp.AUTO);
          if (image.getHeight() > 1500) image.resize(Jimp.AUTO, 1500);
          image.quality(40);
          image.getBufferAsync(Jimp.MIME_JPEG).then(buffer => {
            processedFiles.front = new Blob([buffer]);
            if (!props.currentDocType.hasBack) {
              submitPhotos(processedFiles);
              return;
            }
            let readerBack = new FileReader();
            readerBack.readAsArrayBuffer(Files.back);
            readerBack.onloadend = function () {
              Jimp.read(readerBack.result)
                .then(image => {
                  if (image.getWidth() > 1500) image.resize(1500, Jimp.AUTO);
                  if (image.getHeight() > 1500) image.resize(Jimp.AUTO, 1500);
                  image.quality(40);
                  image.getBufferAsync(Jimp.MIME_JPEG).then(buffer => {
                    processedFiles.back = new Blob([buffer]);
                    submitPhotos(processedFiles);
                  });
                })
                .catch(error => {
                  console.error("Error procesando dorso:");
                  const newErrors = { ...ErrorValidation };
                  newErrors.back = "unsupported_format";
                  setErrorValidation(newErrors);
                  setSubmitting(false);
                  toast.error(<FormattedMessage id="photo.errors.unsupported_format" />);
                });
            };
          });
        })
        .catch(error => {
          console.error("Error procesando frente:");
          const newErrors = { ...ErrorValidation };
          newErrors.front = "unsupported_format";
          setErrorValidation(newErrors);
          setSubmitting(false);
          toast.error(<FormattedMessage id="photo.errors.unsupported_format" />);
        });
    };
  }

  // Falla al subir un lado. Devuelve el error que corta el envío, con el texto del toast.
  // - Pasajera (red, 429, 5xx): la foto está bien; no se marca error y se reintenta con la misma.
  // - Rechazo: guarda el motivo para el aviso de abajo y limpia los inputs del lado, para elegir
  //   otra foto (el botón queda deshabilitado hasta entonces).
  function rejectSide(side, uploadError) {
    const rejection = uploadRejection(uploadError);
    const failed = new Error(`${side}_failed`);
    if (rejection.transient) {
      failed.userMessage = rejection.retryAfter ? (
        <FormattedMessage
          id="login.errors.too_many_attempts"
          values={{ minutes: Math.ceil(rejection.retryAfter / 60) }}
        />
      ) : (
        <FormattedMessage id="photo.errors.retry" />
      );
      return failed;
    }
    setErrorValidation(prev => ({ ...prev, [side]: "api_error" }));
    setErrorMessages(prev => ({ ...prev, [side]: rejection.message }));
    const inputs =
      side === "front"
        ? [frontInput.current, frontCameraInput.current]
        : [backInput.current, backCameraInput.current];
    inputs.forEach(input => {
      if (input) input.value = "";
    });
    failed.userMessage = rejection.message;
    return failed;
  }

  async function submitPhotos(processedFiles) {
    try {
      let frontResult, backResult;

      try {
        frontResult = props.currentDocType.hasFront
          ? await fileUpload("frente", processedFiles.front, props.paxId)
          : true;
      } catch (frontError) {
        console.error("Error en el frente del documento:");
        throw rejectSide("front", frontError);
      }

      try {
        backResult = props.currentDocType.hasBack
          ? await fileUpload("dorso", processedFiles.back, props.paxId)
          : true;
      } catch (backError) {
        console.error("Error en el dorso del documento:");
        throw rejectSide("back", backError);
      }

      if (!frontResult || !backResult) throw new Error("submiterror");

      let sides = [frontResult, backResult];
      let currentPaxData = { ...LocalPaxData[props.paxId] };
      let paxd = await fetchData("PaxsData", CIDData.IDInbox);
      setPaxsData(paxd);
      let currentPax = paxd.list.find(pax => pax.paxid == props.paxId);
      currentPaxData.documents = currentPax.documents;
      currentPaxData.has_document_front = currentPax.has_document_front;
      currentPaxData.has_document_back = currentPax.has_document_back;
      let erred = false;
      let newFiles = { ...Files };
      let newPreviews = { ...Previews };
      let newErrorValidation = { ...ErrorValidation };

      sides.forEach((side, index) => {
        if (side.error || side.response == 0) {
          let errorCode = index === 0 ? "front" : "back";
          console.error("Error processing img", errorCode);
          newPreviews = { ...newPreviews, [errorCode]: Previews[errorCode] }; // Mantenemos la vista previa
          newFiles = { ...newFiles, [errorCode]: Files[errorCode] }; // Mantenemos el archivo
          newErrorValidation = {
            ...newErrorValidation,
            [errorCode]: "api_error"
          };
          if (errorCode === "front") frontInput.current.value = "";
          if (errorCode === "back") backInput.current.value = "";
          erred = true;
          toast.error(
            <FormattedMessage
              id={`photo.errors.${errorCode}`}
              defaultMessage="Ha ocurrido un error desconocido"
            />
          );
        }
        if (side.result && !side.error) {
          if (
            side.result.expiry &&
            moment(side.result.expiry) &&
            moment(side.result.expiry) > moment()
          ) {
            if (
              side.result.documentNumber &&
              side.result.documentNumber !== ""
            ) {
              if (side.result.documentNumber)
                currentPaxData.idnumber = side.result.documentNumber;
              if (side.result.firstName)
                currentPaxData.firstname = toTitleCase(side.result.firstName);
              if (side.result.lastName)
                currentPaxData.lastname = toTitleCase(side.result.lastName);
              if (side.result.nationality_full)
                currentPaxData.nationality = side.result.nationality_full;
              if (side.result.nationality_full)
                currentPaxData.country = side.result.nationality_full;
              if (side.result.dob && moment(side.result.dob))
                currentPaxData.birthdate = moment(side.result.dob).format(
                  "YYYY-MM-DD"
                );
              if (side.result.nationality_full)
                currentPaxData.nationality = side.result.nationality_full;
            }
          }
        }
      });
      if (erred) {
        setFiles(newFiles);
        setPreviews(newPreviews);
        setErrorValidation(newErrorValidation);
        throw "fail";
      }
      setLocalPaxData({ ...LocalPaxData, [props.paxId]: currentPaxData });
      // Documento vencido hace menos de 12 meses: se acepta, pero el huésped lo tiene que ver.
      const warning = uploadWarning(frontResult);
      if (warning) toast.warn(warning.message, { autoClose: 20000 });
      else toast.success(<FormattedMessage id="photo.success" />);
      setSubmitting(false);
      setRedirectOn(true);
      return "Exito";
    } catch (err) {
      setSubmitting(false);
      if (
        err !== "fail" &&
        err.message !== "front_failed" &&
        err.message !== "back_failed"
      ) {
        toast.error(
          <FormattedMessage
            id={`photo.errors.${err}`}
            defaultMessage="Ha ocurrido un error desconocido"
          />
        );
      } else if (err.message === "front_failed") {
        toast.error(
          err.userMessage || (
            <FormattedMessage
              id="photo.errors.front"
              defaultMessage="Error en el frente del documento"
            />
          )
        );
      } else if (err.message === "back_failed") {
        toast.error(
          err.userMessage || (
            <FormattedMessage
              id="photo.errors.back"
              defaultMessage="Error en el dorso del documento"
            />
          )
        );
      }
    }
  }

  const handleSubmit = () => {
    setSubmitting(true);

    submitDocs().catch(err => {
      setSubmitting(false);
      if (err !== "fail") {
        toast.error(
          <FormattedMessage
            id={`photo.errors.${err}`}
            defaultMessage="Ha ocurrido un error desconocido"
          />
        );
      }
    });
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Grid container spacing={1}>
          {props.currentDocType.hasFront && (
            <Grid item xs={12}>
              <Box pt={2} pb={2}>
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Box
                      display="flex"
                      p={1}
                      justifyContent="center"
                      alignItems="center"
                    >
                      {LoadingPreviews.front ? (
                        <CircularProgress />
                      ) : (
                        <img
                          src={
                            Previews.front
                              ? Previews.front
                              : props.currentDocType.frontExample
                          }
                          style={{ width: "100%" }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="h6">
                      <strong>
                        <FormattedMessage id="photo.front" />
                        {Previews.front && !ErrorValidation.front && (
                          <CheckCircleOutlineIcon
                            htmlColor="#4DD824"
                            fontSize="small"
                            style={{
                              verticalAlign: "text-bottom",
                              marginLeft: "5px"
                            }}
                          />
                        )}
                        {ErrorValidation.front && (
                          <ErrorOutlineIcon
                            htmlColor="#F44336"
                            fontSize="small"
                            style={{
                              verticalAlign: "text-bottom",
                              marginLeft: "5px"
                            }}
                          />
                        )}
                      </strong>
                    </Typography>
                    <Typography
                      gutterBottom
                      variant="body2"
                      color="textSecondary"
                    >
                      {LoadingPreviews.front ? (
                        <FormattedMessage id="main.loading" />
                      ) : ErrorValidation.front ? (
                        <span style={{ color: "#F44336" }}>
                          <FormattedMessage
                            id={`photo.errors.${ErrorValidation.front}`}
                            defaultMessage="Error en la imagen"
                          />
                        </span>
                      ) : (
                        <FormattedMessage
                          id={props.currentDocType.frontTextId}
                        />
                      )}
                    </Typography>

                    <Box display="flex" flexWrap="wrap">
                      <Box mr={1} mb={1}>
                        <label htmlFor="front-upload">
                          <Button
                            variant="outlined"
                            disabled={LoadingPreviews.front || LoadingPreviews.back}
                            color="inherit"
                            size="small"
                            component="span"
                          >
                            <CloudUploadIcon />{" "}
                            <Box pl={1}>
                              <FormattedMessage
                                id={
                                  Previews.front && !ErrorValidation.front
                                    ? "photo.switchFront"
                                    : "photo.uploadFront"
                                }
                              />
                            </Box>
                          </Button>
                        </label>
                      </Box>
                      {HasCamera && (
                        <Box mb={1}>
                          <label htmlFor="front-camera">
                            <Button
                              variant="outlined"
                              disabled={LoadingPreviews.front || LoadingPreviews.back}
                              color="inherit"
                              size="small"
                              component="span"
                            >
                              <PhotoCameraIcon />{" "}
                              <Box pl={1}>
                                <FormattedMessage id="photo.takePhoto" />
                              </Box>
                            </Button>
                          </label>
                        </Box>
                      )}
                    </Box>
                    <input
                      ref={frontInput}
                      style={{ display: "none" }}
                      accept="image/*"
                      id="front-upload"
                      type="file"
                      onChange={e => handleChange(e, "front")}
                    />
                    {HasCamera && (
                      <input
                        style={{ display: "none" }}
                        accept="image/*"
                        capture="environment"
                        ref={frontCameraInput}
                        id="front-camera"
                        type="file"
                        onChange={e => handleChange(e, "front")}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}
          {props.currentDocType.hasFront && props.currentDocType.hasBack && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}
          {props.currentDocType.hasBack && (
            <Grid item xs={12}>
              <Box pt={2} pb={2}>
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Box
                      display="flex"
                      p={1}
                      justifyContent="center"
                      alignItems="center"
                    >
                      {LoadingPreviews.back ? (
                        <CircularProgress />
                      ) : (
                        <img
                          src={
                            Previews.back
                              ? Previews.back
                              : props.currentDocType.backExample
                          }
                          style={{ width: "100%" }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="h6">
                      <strong>
                        <FormattedMessage id="photo.back" />
                        {Previews.back && !ErrorValidation.back && (
                          <CheckCircleOutlineIcon
                            htmlColor="#4DD824"
                            fontSize="small"
                            style={{
                              verticalAlign: "text-bottom",
                              marginLeft: "5px"
                            }}
                          />
                        )}
                        {ErrorValidation.back && (
                          <ErrorOutlineIcon
                            htmlColor="#F44336"
                            fontSize="small"
                            style={{
                              verticalAlign: "text-bottom",
                              marginLeft: "5px"
                            }}
                          />
                        )}
                      </strong>
                    </Typography>
                    <Typography
                      gutterBottom
                      variant="body2"
                      color="textSecondary"
                    >
                      {LoadingPreviews.back ? (
                        <FormattedMessage id="main.loading" />
                      ) : ErrorValidation.back ? (
                        <span style={{ color: "#F44336" }}>
                          <FormattedMessage
                            id={`photo.errors.${ErrorValidation.back}`}
                            defaultMessage="Error en la imagen"
                          />
                        </span>
                      ) : (
                        <FormattedMessage
                          id={props.currentDocType.backTextId}
                        />
                      )}
                    </Typography>

                    <Box display="flex" flexWrap="wrap">
                      <Box mr={1} mb={1}>
                        <label htmlFor="back-upload">
                          <Button
                            variant="outlined"
                            disabled={LoadingPreviews.front || LoadingPreviews.back}
                            color="inherit"
                            size="small"
                            component="span"
                          >
                            <CloudUploadIcon />{" "}
                            <Box pl={1}>
                              <FormattedMessage
                                id={
                                  Previews.back && !ErrorValidation.back
                                    ? "photo.switchBack"
                                    : "photo.uploadBack"
                                }
                              />
                            </Box>
                          </Button>
                        </label>
                      </Box>
                      {HasCamera && (
                        <Box mb={1}>
                          <label htmlFor="back-camera">
                            <Button
                              variant="outlined"
                              disabled={LoadingPreviews.front || LoadingPreviews.back}
                              color="inherit"
                              size="small"
                              component="span"
                            >
                              <PhotoCameraIcon />{" "}
                              <Box pl={1}>
                                <FormattedMessage id="photo.takePhoto" />
                              </Box>
                            </Button>
                          </label>
                        </Box>
                      )}
                    </Box>
                    <input
                      ref={backInput}
                      style={{ display: "none" }}
                      accept="image/*"
                      id="back-upload"
                      type="file"
                      onChange={e => handleChange(e, "back")}
                    />
                    {HasCamera && (
                      <input
                        style={{ display: "none" }}
                        accept="image/*"
                        capture="environment"
                        ref={backCameraInput}
                        id="back-camera"
                        type="file"
                        onChange={e => handleChange(e, "back")}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>
      {!(
        (!Files.front && props.currentDocType.hasFront) ||
        (!Files.back && props.currentDocType.hasBack) ||
        ErrorValidation.front ||
        ErrorValidation.back
      ) ? (
        <Grid item xs={12}>
          <Box pt={1} pb={2}>
            <Alert severity="success">
              <AlertTitle>
                <FormattedMessage id="photo.readyTitle" />
              </AlertTitle>
              <FormattedMessage id="photo.ready" />
            </Alert>
          </Box>
        </Grid>
      ) : ErrorValidation.front || ErrorValidation.back ? (
        <Grid item xs={12}>
          <Box pt={1} pb={2}>
            <Alert severity="error">
              <AlertTitle>
                <FormattedMessage id="photo.errorMessage.errorTitle" />
              </AlertTitle>
              {ErrorMessages.front || ErrorMessages.back ? (
                // Por lado: el motivo de api2 si lo hay; si no, el texto traducido de ese lado.
                ["front", "back"]
                  .filter(side => ErrorValidation[side])
                  .map(side => (
                    <div key={side}>
                      {ErrorMessages[side] || (
                        <FormattedMessage id={`photo.errorMessage.${side}`} />
                      )}
                    </div>
                  ))
              ) : (
                <FormattedMessage
                  id={
                    ErrorValidation.front && ErrorValidation.back
                      ? "photo.errorMessage.frontback"
                      : ErrorValidation.front
                      ? "photo.errorMessage.front"
                      : "photo.errorMessage.back"
                  }
                />
              )}
            </Alert>
          </Box>
        </Grid>
      ) : (
        ""
      )}
      <Grid item xs={12} className="ButtonWrapper">
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          disabled={
            Submitting ||
            (!Files.front && props.currentDocType.hasFront) ||
            (!Files.back && props.currentDocType.hasBack) ||
            ErrorValidation.front ||
            ErrorValidation.back
          }
          endIcon={Submitting ? null : <ChevronRightIcon />}
          onClick={handleSubmit}
        >
          <FormattedMessage
            id={!Submitting ? "photo.submit" : "photo.submitting"}
          />
        </Button>
        {Submitting && (
          <CircularProgress size={24} className="ButtonProgress" />
        )}
      </Grid>
      {RedirectOn ? (
        <Redirect
          push
          to={`${LngLink + process.env.REACT_APP_ROOT_PATH_LINK}/checkin/${
            props.cid
          }/paxs/${props.paxId}`}
        />
      ) : (
        ""
      )}
    </Grid>
  );
};

export default PhotoUpload;
